import { createReducer, getType } from 'typesafe-actions';

import { PileSections, PileState, SortOption } from './types';
import * as actions from './actions';
import {
  keepUnchanged,
  toCompletedTask,
  toDoLaterTask,
  toNoDueDateTask,
  toToDoTask,
  toRecentlyCreatedTask
} from './converters';
import { DeletePlannedTaskResponse, PileFilterType, PileStateOptions } from 'shovel-lib/types';
import * as calendarActions from '../calendar/actions';
import { deleteTaskCategory, setCategoryMinutes, updateTaskCategory } from '../course/actions';
import * as taskActions from '../task/actions';
import { getUserSettings, saveUserSettings } from '../settings/actions';
import { now, toMomentDate } from 'shovel-lib/utils/timeUtils';
import { timerActions } from '../timer';
import { tasksUndone } from '../undo/actions';
import storage from '@utils/storage';

export const initialFilterState: PileFilterType = {
  //TODO: for now on BE types are ignored
  types: [],
  courseIds: [],
  priorities: [],
  negativeCushionMarked: false,
  hidePlannedMarked: false,
  examMarked: false,
  next7Days: false,
  nextWeek: false,
  thisWeek: false,
  lastWeek: false,
  last30Days: false
};

export const initialSortState = {
  todo: {
    sortField: SortOption.DUE_DATE,
    desc: false
  },
  completed: {
    sortField: SortOption.DATE_COMPLETED,
    desc: true
  }
};

const initialState: PileState = {
  activeTab: PileStateOptions.ACTIVE,
  courses: [],
  filter: storage.getPileFilter() || initialFilterState,
  sort: storage.getPileSort() || initialSortState,
  search: '',
  allToDo: {
    content: [],
    last: true,
    totalPages: 0,
    totalElements: 0,
    size: 0,
    sort: null,
    first: true,
    numberOfElements: 0,
    page: 0
  },
  withinStartDate: {
    tasks: []
  },
  completed: {
    tasks: []
  },
  [PileSections.OVERDUE]: {
    expanded: false,
    tasks: []
  },
  [PileSections.NO_DUE_DATE]: {
    expanded: false,
    tasks: []
  },
  moveToDoDialog: {
    open: false,
    data: {
      dueDate: '',
      daysToStartAhead: -1,
      taskId: -1
    }
  },
  [PileSections.RECENTLY_CREATED]: {
    tasks: [],
    expanded: false
  },
  hiddenTaskIds: []
};

const reducer = createReducer(initialState)
  .handleAction(actions.hideCompletedTask, (state, { payload }) => ({
    ...state,
    hiddenTaskIds: [...(state.hiddenTaskIds || []).filter(id => id !== payload), payload]
  }))
  .handleAction(actions.showHiddenTask, (state, { payload }) => ({
    ...state,
    hiddenTaskIds: (state.hiddenTaskIds || []).filter(id => id !== payload)
  }))
  // A card hidden as it was completed shows again when the completion is undone, even before the list refreshes.
  .handleAction(tasksUndone, (state, { payload }) => {
    const undone = new Set(payload.tasks.map(task => task.taskId));
    return { ...state, hiddenTaskIds: (state.hiddenTaskIds || []).filter(id => !undone.has(id)) };
  })
  .handleAction(actions.getCourses.success, (state, action) => ({ ...state, courses: action.payload }))

  .handleAction(actions.applyFilter, (state, action) => {
    const { payload } = action;
    return {
      ...state,
      filter: payload.filter,
      sort: payload.sort
    };
  })
  .handleAction(actions.applySearch, (state, action) => {
    const { payload } = action;
    return {
      ...state,
      search: payload,
      [PileSections.OVERDUE]: {
        ...state[PileSections.OVERDUE],
        expanded: !!payload
      },
      [PileSections.NO_DUE_DATE]: {
        ...state[PileSections.NO_DUE_DATE],
        expanded: !!payload
      },
      [PileSections.RECENTLY_CREATED]: {
        ...state[PileSections.RECENTLY_CREATED],
        expanded: !!payload
      }
    };
  })

  .handleAction(actions.clearState, () => initialState)

  .handleAction(actions.clearStateButKeepFilterAndSort, state => ({
    ...initialState,
    filter: state.filter,
    sort: state.sort
  }))

  .handleAction(actions.getAllToDoTasks.success, (state, action) => {
    const { isRefresh, ...payload } = action.payload;
    const tasks = payload.content.map(t => toToDoTask(t));
    return {
      ...state,
      allToDo: {
        ...(isRefresh ? { ...state.allToDo, last: payload.last } : payload),
        content: payload.page === 0 ? keepUnchanged(state.allToDo.content, tasks) : state.allToDo.content.concat(tasks)
      }
    };
  })

  .handleAction(actions.getWithinStartDateTasks.success, (state, action) => ({
    ...state,
    withinStartDate: {
      ...state.withinStartDate,
      tasks: keepUnchanged(
        state.withinStartDate.tasks,
        action.payload.map(t => toToDoTask(t))
      )
    }
  }))

  .handleAction(actions.getOverdueTasks.success, (state, action) => ({
    ...state,
    [PileSections.OVERDUE]: {
      ...state[PileSections.OVERDUE],
      tasks: keepUnchanged(
        state[PileSections.OVERDUE].tasks,
        action.payload.map(t => toDoLaterTask(t))
      )
    }
  }))

  .handleAction(actions.getCompletedTasks.success, (state, action) => ({
    ...state,
    completed: {
      ...state.completed,
      tasks: keepUnchanged(
        state.completed.tasks,
        action.payload.map(t => toCompletedTask(t))
      )
    }
  }))
  .handleAction(actions.openMoveToDoDialog, (state, action) => ({
    ...state,
    moveToDoDialog: {
      open: true,
      data: action.payload
    }
  }))
  .handleAction(actions.closeMoveToDoDialog, (state, action) => ({
    ...state,
    moveToDoDialog: initialState.moveToDoDialog
  }))
  .handleAction(actions.getNoDueDateTasks.success, (state, action) => ({
    ...state,
    [PileSections.NO_DUE_DATE]: {
      ...state[PileSections.NO_DUE_DATE],
      tasks: keepUnchanged(
        state[PileSections.NO_DUE_DATE].tasks,
        action.payload.map(t => toNoDueDateTask(t))
      )
    }
  }))
  .handleAction(calendarActions.updateCourse.success, (state, action) => {
    const { id, colorHex, name } = action.payload;
    const mapFn = task => {
      if (task.courseId !== id) return task;
      return { ...task, colorHex, courseName: name };
    };
    return newPileStateMap(state, mapFn);
  })
  .handleAction(updateTaskCategory.success, (state, action) => {
    const { id, name, emoji } = action.payload;
    const mapFn = task => {
      if (task.categoryId !== id) return task;
      return { ...task, emoji, categoryName: name };
    };
    return newPileStateMap(state, mapFn);
  })
  .handleAction(deleteTaskCategory.success, (state, action) => {
    const mapFn = task => {
      if (task.categoryId !== action.payload.id) return task;
      return { ...task, categoryId: null, emoji: null, categoryName: null };
    };
    return newPileStateMap(state, mapFn);
  })
  .handleAction(taskActions.deleteTask.success, (state, action) => {
    const filterFn = task => task.taskId !== action.payload.id;
    return newPileStateFilter(state, filterFn);
  })
  .handleAction(taskActions.deleteTasks.success, (state, action) => {
    const filterFn = task => !action.payload.ids.includes(task.taskId);
    return newPileStateFilter(state, filterFn);
  })
  .handleAction(taskActions.setTimeNeeded.success, (state, action) => {
    const { taskIds, timeNeeded, startAhead } = action.payload;
    const mapFn = task => {
      return taskIds.includes(task.taskId)
        ? {
            ...task,
            timeNeed: timeNeeded,
            daysToStartAhead: startAhead?.[task.taskId] ?? task.daysToStartAhead
          }
        : task;
    };
    return newPileStateMap(state, mapFn);
  })
  // A category's time given to its tasks still to do patches them as an estimate does.
  .handleAction([taskActions.estimateTasks.success, setCategoryMinutes.success], (state, action) => {
    const { tasks } = action.payload;
    const pileId = estimated => estimated.taskId || estimated.id;
    const isTrashed = estimated => estimated.deleted || estimated.trashed;
    const filterFn = task => !tasks.some(estimated => isTrashed(estimated) && pileId(estimated) === task.taskId);
    const mapFn = task => {
      const estimated = tasks.find(t => pileId(t) === task.taskId);
      if (!estimated) return task;
      return {
        ...task,
        ...(estimated.timeNeed != null ? { timeNeed: estimated.timeNeed } : {}),
        ...(estimated.timeSpent != null ? { timeSpent: estimated.timeSpent } : {})
      };
    };
    return newPileStateMap(newPileStateFilter(state, filterFn), mapFn);
  })
  .handleAction(taskActions.saveManageTask.success, (state, action) => {
    const {
      taskId,
      request,
      response: { timeEntries, ...taskProps }
    } = action.payload;
    // pile will reload in this case
    if (Object(request).hasOwnProperty('dueDate') || Object(request).hasOwnProperty('daysToStartAhead')) {
      return state;
    }
    const mapFn = task => {
      return taskId === task.taskId
        ? {
            ...task,
            ...taskProps,
            subtitle: taskProps.details
          }
        : task;
    };
    return newPileStateMap(state, mapFn);
  })
  // A reading's pages come back with the time they work out to and the start that time needs. The card shows them as soon
  // as the save is through, as it does a time saved in minutes, not once the lists have reloaded: the estimate popup
  // turns its button into the card's time tag while it saves, and the button came back for a moment in between.
  .handleAction(taskActions.savePageRanges.success, (state, action) => {
    const { taskId, data } = action.payload as { taskId: number; data?: { timeNeed?: number; daysToStartAhead?: number } };
    if (!data || data.timeNeed == null) return state;
    const { timeNeed, daysToStartAhead } = data;
    const mapFn = task =>
      task.taskId === taskId ? { ...task, timeNeed, ...(daysToStartAhead != null ? { daysToStartAhead } : {}) } : task;
    return newPileStateMap(state, mapFn);
  })
  .handleAction(calendarActions.updatePlannedTask.success, (state, action) => {
    const { taskId, timeSpent, timeNeed, timePlanned } = action.payload.data;
    const mapFn = task => {
      return taskId === task.taskId
        ? {
            ...task,
            timeNeed,
            timeSpent,
            timePlanned
          }
        : task;
    };
    return newPileStateMap(state, mapFn);
  })
  .handleAction([calendarActions.markAsUsed.success, timerActions.start.success], (state, action) => {
    const { taskId, timeSpent, timeNeed, timePlanned, plannedTaskDto } = action.payload;
    if (!plannedTaskDto) return state;
    const mapFn = task => {
      return taskId === task.taskId
        ? {
            ...task,
            timeNeed,
            timeSpent,
            timePlanned
          }
        : task;
    };
    return newPileStateMap(state, mapFn);
  })
  .handleAction(actions.calculateCushion.success, (state, action) => {
    const cushions = action.payload;
    const withCushion = t => (t.cushion === cushions[t.taskId] ? t : { ...t, cushion: cushions[t.taskId] });
    const allToDo =
      state.activeTab === PileStateOptions.ACTIVE
        ? {
            ...state.allToDo,
            content: state.allToDo.content.map(withCushion)
          }
        : state.allToDo;
    const withinStartDate =
      state.activeTab === PileStateOptions.UPCOMING
        ? {
            tasks: state.withinStartDate.tasks.map(withCushion)
          }
        : state.withinStartDate;
    return {
      ...state,
      allToDo,
      withinStartDate
    };
  })
  .handleAction(actions.clearCushion, state => {
    // A task with no cushion to clear stays as it is, and so does its card.
    const withoutCushion = t => (t.cushion == null ? t : { ...t, cushion: null });
    return {
      ...state,
      allToDo: {
        ...state.allToDo,
        // @ts-ignore
        content: state.allToDo.content.map(withoutCushion)
      },
      withinStartDate: {
        tasks: state.withinStartDate.tasks.map(withoutCushion)
      }
    };
  })
  .handleAction(taskActions.changeTaskCategory.success, (state, action) => {
    const { taskIds, category } = action.payload;
    const taskCategory = { categoryId: category?.id, categoryName: category?.name, emoji: category?.emoji };
    const mapFunction = t => (taskIds.includes(t.taskId) ? { ...t, ...taskCategory } : t);
    return newPileStateMap(state, mapFunction);
  })
  .handleAction([getUserSettings.success, saveUserSettings.success], (state, action) => {
    if (action.payload) {
      return {
        ...state,
        activeTab: action.payload.pileState
      };
    }
    return state;
  })
  .handleAction([calendarActions.planTask.success, calendarActions.planTasks.success], (state, action) => {
    if (state.activeTab === PileStateOptions.COMPLETED) {
      return state; // cannot plan completed tasks so there's no need to refresh completed pile
    }

    const plannedTasks = action.type === getType(calendarActions.planTask.success) ? [action.payload] : action.payload;
    const mapFunction = t => {
      const plannedTask = plannedTasks.find(pt => pt.taskId === t.taskId);
      if (!plannedTask || now().isAfter(plannedTask.end)) return t;
      const timePlanned = (t.timePlanned || 0) + toMomentDate(plannedTask.end).diff(plannedTask.start, 'minutes');
      return { ...t, timePlanned };
    };
    return newPileStateMap(state, mapFunction);
  })
  .handleAction(calendarActions.deletePlannedTask.success, (state, action) => {
    const {
      event: { taskId, start, end, isUsed },
      data: { timeNeed, timeSpent, timePlanned }
    } = action.payload;

    if (state.activeTab === PileStateOptions.COMPLETED || now().isAfter(end)) {
      return state;
    }
    let mapFunction;
    if (isUsed) {
      mapFunction = t =>
        t.taskId === taskId
          ? {
              ...t,
              timeNeed,
              timeSpent,
              timePlanned
            }
          : t;
    } else {
      const plannedMinutes = toMomentDate(end).diff(start, 'minutes');
      mapFunction = t =>
        t.taskId === taskId
          ? {
              ...t,
              timePlanned: Math.max(t.timePlanned - plannedMinutes, 0)
            }
          : t;
    }

    return newPileStateMap(state, mapFunction);
  })
  .handleAction(calendarActions.markBlocksUsed.success, (state, action) => {
    const { tasks } = action.payload;
    if (state.activeTab === PileStateOptions.COMPLETED || tasks.length === 0) {
      return state;
    }
    const dataByTaskId = new Map<number, DeletePlannedTaskResponse>(tasks.map(data => [data.taskId, data]));
    const mapFn = task => {
      const data = dataByTaskId.get(task.taskId);
      return data ? { ...task, timeNeed: data.timeNeed, timeSpent: data.timeSpent, timePlanned: data.timePlanned } : task;
    };
    return newPileStateMap(state, mapFn);
  })
  .handleAction(calendarActions.unplanWeek.success, (state, action) => {
    const { events } = action.payload;
    if (state.activeTab === PileStateOptions.COMPLETED || events.length === 0) {
      return state;
    }
    const minutesByTaskId = new Map<number, number>();
    events.forEach(({ taskId, start, end }) => {
      minutesByTaskId.set(taskId, (minutesByTaskId.get(taskId) || 0) + toMomentDate(end).diff(start, 'minutes'));
    });
    const mapFunction = t =>
      minutesByTaskId.has(t.taskId) ? { ...t, timePlanned: Math.max(t.timePlanned - minutesByTaskId.get(t.taskId)!, 0) } : t;
    return newPileStateMap(state, mapFunction);
  })
  .handleAction(calendarActions.movePlannedTask.success, (state, action) => {
    const { oldEvent, newEvent } = action.payload;
    if (state.activeTab === PileStateOptions.COMPLETED) {
      return state;
    }

    const today = now();
    const oldPlannedMinutes = toMomentDate(oldEvent.end).diff(oldEvent.start, 'minutes');
    const newPlannedMinutes = toMomentDate(newEvent.end).diff(newEvent.start, 'minutes');
    const timePlannedDiff =
      (today.isSameOrBefore(newEvent.end) ? newPlannedMinutes : 0) -
      (today.isSameOrBefore(oldEvent.end) ? oldPlannedMinutes : 0);

    const mapFunction = t =>
      t.taskId === newEvent.taskId
        ? {
            ...t,
            timePlanned: newEvent.isUsed ? t.timePlanned : Math.max(t.timePlanned + timePlannedDiff, 0)
          }
        : t;

    return newPileStateMap(state, mapFunction);
  })
  .handleAction(calendarActions.resizePlannedTask.success, (state, action) => {
    const { oldEvent, newEvent } = action.payload;
    if (state.activeTab === PileStateOptions.COMPLETED) {
      return state;
    }

    const today = now();
    const oldPlannedMinutes = toMomentDate(oldEvent.end).diff(oldEvent.start, 'minutes');
    const newPlannedMinutes = toMomentDate(newEvent.end).diff(newEvent.start, 'minutes');
    const timePlannedDiff =
      (today.isSameOrBefore(newEvent.end) ? newPlannedMinutes : 0) -
      (today.isSameOrBefore(oldEvent.end) ? oldPlannedMinutes : 0);

    const { timeNeed, timeSpent, isUsed } = newEvent;
    const mapFunction = t =>
      t.taskId === newEvent.taskId
        ? {
            ...t,
            timePlanned: isUsed ? t.timePlanned : Math.max(t.timePlanned + timePlannedDiff, 0),
            timeNeed: isUsed ? timeNeed : t.timeNeed,
            timeSpent: isUsed ? timeSpent : t.timeSpent
          }
        : t;

    return newPileStateMap(state, mapFunction);
  })
  .handleAction(taskActions.addPlannedTask.success, (state, action) => {
    const { taskId, timePlanned } = action.payload;
    const mapFunction = t =>
      t.taskId === taskId
        ? {
            ...t,
            timePlanned: Math.max(t.timePlanned + timePlanned, 0)
          }
        : t;

    return newPileStateMap(state, mapFunction);
  })
  .handleAction(actions.getRecentlyCreated.success, (state, action) => ({
    ...state,
    [PileSections.RECENTLY_CREATED]: {
      ...state[PileSections.RECENTLY_CREATED],
      tasks: keepUnchanged(
        state[PileSections.RECENTLY_CREATED].tasks,
        action.payload.map(t => toRecentlyCreatedTask(t))
      )
    }
  }))
  .handleAction(taskActions.createTasks.success, (state, action) => ({
    ...state,
    [PileSections.RECENTLY_CREATED]: {
      ...state[PileSections.RECENTLY_CREATED],
      expanded: action.payload.isFromSidebar || state[PileSections.RECENTLY_CREATED].expanded
    }
  }))
  .handleAction(actions.toggleExpandPileSection, (state, action) => ({
    ...state,
    [action.payload]: {
      ...state[action.payload],
      expanded: !state[action.payload].expanded
    }
  }));

const newPileStateMap = (state, mapFn) => {
  const completedActive = state.activeTab === PileStateOptions.COMPLETED;
  const allTodoActive = !completedActive && state.activeTab === PileStateOptions.ACTIVE;
  const upcomingActive = !allTodoActive && state.activeTab === PileStateOptions.UPCOMING;

  return {
    ...state,
    allToDo: allTodoActive
      ? {
          ...state.allToDo,
          content: state.allToDo.content.map(mapFn)
        }
      : state.allToDo,
    withinStartDate: upcomingActive ? { tasks: state.withinStartDate.tasks.map(mapFn) } : state.withinStartDate,
    completed: completedActive ? { tasks: state.completed.tasks.map(mapFn) } : state.completed,
    [PileSections.OVERDUE]: !completedActive
      ? { ...state[PileSections.OVERDUE], tasks: state[PileSections.OVERDUE].tasks.map(mapFn) }
      : state[PileSections.OVERDUE],
    [PileSections.NO_DUE_DATE]: !completedActive
      ? { ...state[PileSections.NO_DUE_DATE], tasks: state[PileSections.NO_DUE_DATE].tasks.map(mapFn) }
      : state[PileSections.NO_DUE_DATE],
    [PileSections.RECENTLY_CREATED]: !completedActive
      ? { ...state[PileSections.RECENTLY_CREATED], tasks: state[PileSections.RECENTLY_CREATED].tasks.map(mapFn) }
      : state[PileSections.RECENTLY_CREATED]
  };
};

const newPileStateFilter = (state, filterFn) => ({
  ...state,
  allToDo: {
    ...state.allToDo,
    content: state.allToDo.content.filter(filterFn)
  },
  withinStartDate: { tasks: state.withinStartDate.tasks.filter(filterFn) },
  completed: { tasks: state.completed.tasks.filter(filterFn) },
  [PileSections.OVERDUE]: {
    ...state[PileSections.OVERDUE],
    tasks: state[PileSections.OVERDUE].tasks.filter(filterFn)
  },
  [PileSections.NO_DUE_DATE]: {
    ...state[PileSections.NO_DUE_DATE],
    tasks: state[PileSections.NO_DUE_DATE].tasks.filter(filterFn)
  },
  [PileSections.RECENTLY_CREATED]: {
    ...state[PileSections.RECENTLY_CREATED],
    tasks: state[PileSections.RECENTLY_CREATED].tasks.filter(filterFn)
  }
});

/**
 * A hidden task is let go once no list holds it any more: after the refresh that follows its save. Letting go any
 * sooner, a list not yet refreshed would show it again for a moment.
 */
const releaseHidden = (state: PileState): PileState => {
  const hidden = state.hiddenTaskIds;
  if (!hidden || hidden.length === 0) return state;
  const held = new Set<number>();
  [
    state.allToDo.content,
    state.withinStartDate.tasks,
    state[PileSections.OVERDUE].tasks,
    state[PileSections.NO_DUE_DATE].tasks,
    state[PileSections.RECENTLY_CREATED].tasks
  ].forEach(list => (list || []).forEach((task: { taskId: number }) => held.add(task.taskId)));
  const kept = hidden.filter(id => held.has(id));
  return kept.length === hidden.length ? state : { ...state, hiddenTaskIds: kept };
};

export default (state: PileState | undefined, action: any): PileState => releaseHidden(reducer(state, action));
